/**
 * SATELLAI GA4 custom tracking.
 *
 * 该脚本集中处理飞书埋点表中的站内可实现事件，统一向 dataLayer 推送 ga4Event。
 */
(function () {
  if (window.SatellaiGA4Tracking) return;

  // dataLayer 固定事件名：GTM 侧用它作为统一触发入口，真实业务事件放在 event_name 中。
  const DATA_LAYER_EVENT = 'Datalayer_Event_Triggered';
  // GA4 items[].item_brand 统一品牌值，避免各处硬编码拼写不一致。
  const BRAND_NAME = 'SATELLAI';
  // Quiz 弹窗固定模块前缀；步骤标题会在后面拼接，保持和飞书传值说明一致。
  const QUIZ_MODULE_START = 'will it work for me_start';

  // 站内其他脚本发出的自定义事件名：业务脚本只负责在成功点 dispatch，本文件统一转成 GA4 事件。
  const CUSTOM_EVENTS = Object.freeze({
    PRODUCT_ADDED: 'gtm:product-added',
    CART_QUANTITY_CHANGED: 'gtm:cart-quantity-changed',
    CHECKOUT_CLICK: 'gtm:checkout-click',
    SHIPPING_CALCULATED: 'gtm:shipping-calculated',
    ACCOUNT_AUTHENTICATED: 'gtm:account-authenticated',
    DETAILS_CLICKED: 'gtm:details-clicked',
    NEWSLETTER_CLICKED: 'gtm:newsletter-clicked',
    NEWSLETTER_SUBMITTED: 'gtm:newsletter-submitted',
    ANCHOR_CLICKED: 'gtm:anchor-clicked'
  });

  // 飞书埋点表中的 GA4 业务事件名：后续推送 event_name 时都从这里引用。
  const GA4_EVENTS = Object.freeze({
    ADD_TO_CART: 'add_to_cart',
    BUTTON_CLICK: 'button_click',
    CHECKOUT: 'checkout',
    CLICK_BANNER: 'click_banner',
    CLICK_FUNCTION: 'click_function',
    CLICK_ANCHOR: 'click_anchor',
    CLICK_NAVIGATION: 'click_navigation',
    CLICK_SUBSCRIBE: 'click_subscribe',
    DETAILS_CLICK: 'details_click',
    FOLLOW_US: 'follow_us',
    LOGIN: 'login',
    REMOVE_FROM_CART: 'remove_from_cart',
    SELECT_ITEM: 'select_item',
    SIGN_UP: 'sign_up',
    SUBSCRIBE: 'subscribe',
    TAB_CLICK: 'tab_click',
    VIDEO_COMPLETE: 'video_complete',
    VIDEO_PROGRESS: 'video_progress',
    VIDEO_START: 'video_start',
    VIEW_BANNER: 'view_banner',
    VIEW_CART: 'view_cart',
    VIEW_ITEM: 'view_item',
    VIEW_ITEM_LIST: 'view_item_list'
  });

  class SatellaiGA4Tracking {
    /**
     * 初始化追踪实例的运行态。
     * context 来自 theme.liquid 注入的 Shopify/页面上下文；WeakSet/WeakMap 用于曝光和视频进度去重。
     * click handler 在这里提前 bind，方便 addEventListener 使用同一个函数引用。
     */
    constructor() {
      this.context = window.SatellaiGA4Context || {};
      this.productCache = new Map();
      this.observers = [];
      this.viewedElements = new WeakSet();
      this.observedBannerElements = new WeakSet();
      window.SatellaiViewedBannerKeys = window.SatellaiViewedBannerKeys instanceof Set ? window.SatellaiViewedBannerKeys : new Set();
      this.viewedBannerKeys = window.SatellaiViewedBannerKeys;
      this.bannerMutationTimers = new WeakMap();
      this.videoStates = new WeakMap();
      this.cartDrawerWasOpen = false;
      this.observedCartDrawer = null;
      this.lastCartDrawerOpenAt = 0;
      this.lastXcottonCartTrackKey = '';
      this.lastXcottonCartTrackedAt = 0;
      this.xcottonCartSnapshot = null;
      this.xcottonCartChangeToken = 0;
      this.observedXcottonSwitch = null;
      this.lastCartViewAt = 0;
      this.lastProductViewVariantId = '';
      this.debug = Boolean(this.context.debug);
      this.boundHandleClick = this.handleClick.bind(this);
    }

    /**
     * 启动全站埋点。
     * 首屏会立即检查 PDP 商品曝光、购物车页曝光等同步场景；
     * 再注册点击监听、曝光监听和 DOM 变更监听，覆盖后续异步渲染出的商品卡片和视频。
     */
    init() {
      this.trackProductView();
      this.trackCartPageView();
      this.observeBanners();
      this.observeProductLists();
      this.observeCartDrawer();
      this.observeXcottonShippingProtection();
      this.attachVideos();
      this.patchHistoryForVariantView();
      this.bindCustomEvents();

      document.addEventListener('click', this.boundHandleClick, true);

      const mutationObserver = new MutationObserver(() => {
        this.observeBanners();
        this.observeProductLists();
        this.observeCartDrawer();
        this.observeXcottonShippingProtection();
        this.attachVideos();
      });
      mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
      this.observers.push(mutationObserver);
    }

    /**
     * 监听其他业务脚本抛出的 gtm:* 自定义事件。
     * 这些事件都来自明确的业务分支，例如加购接口成功、购物车数量更新成功、Cognito 登录/注册成功、footer 订阅结果。
     */
    bindCustomEvents() {
      document.addEventListener(CUSTOM_EVENTS.PRODUCT_ADDED, (event) => {
        this.handleProductAdded(event.detail || {});
      });

      document.addEventListener(CUSTOM_EVENTS.CART_QUANTITY_CHANGED, (event) => {
        this.handleCartQuantityChanged(event.detail || {});
      });

      document.addEventListener(CUSTOM_EVENTS.CHECKOUT_CLICK, (event) => {
        this.trackCheckout(event.detail?.button || null);
      });

      document.addEventListener(CUSTOM_EVENTS.SHIPPING_CALCULATED, (event) => {
        const detail = event.detail || {};
        const buttonName = [detail.country, detail.province, detail.zip].filter(Boolean).join('_') || 'calculate';
        const moduleName = `${this.getCartModuleBaseName()}_calculate_complete`;
        this.pushEvent(GA4_EVENTS.BUTTON_CLICK, {
          button_name: buttonName,
          module_name: moduleName
        });
      });

      document.addEventListener(CUSTOM_EVENTS.ACCOUNT_AUTHENTICATED, (event) => {
        const eventName = event.detail?.eventName;
        if (eventName === GA4_EVENTS.LOGIN || eventName === GA4_EVENTS.SIGN_UP) {
          this.pushEvent(eventName, {});
        }
      });

      document.addEventListener(CUSTOM_EVENTS.DETAILS_CLICKED, (event) => {
        const detail = event.detail || {};
        if (!detail.title || !detail.status) return;

        this.pushEvent(GA4_EVENTS.DETAILS_CLICK, {
          title: detail.title,
          module_name: detail.moduleName || 'Product Description',
          status: detail.status
        });
      });

      document.addEventListener(CUSTOM_EVENTS.NEWSLETTER_CLICKED, (event) => {
        const detail = event.detail || {};
        this.pushEvent(GA4_EVENTS.CLICK_SUBSCRIBE, {
          button_name: detail.buttonName || 'Subscribe',
          status: 'click',
          module_name: detail.moduleName || 'bottom navigation'
        });
      });

      document.addEventListener(CUSTOM_EVENTS.NEWSLETTER_SUBMITTED, (event) => {
        const detail = event.detail || {};
        const status = detail.status === 'success' ? 'success' : 'fail';
        this.pushEvent(GA4_EVENTS.SUBSCRIBE, {
          button_name: detail.buttonName || 'Subscribe',
          status,
          module_name: detail.moduleName || 'bottom navigation'
        });
      });

      document.addEventListener(CUSTOM_EVENTS.ANCHOR_CLICKED, (event) => {
        const detail = event.detail || {};
        this.pushEvent(GA4_EVENTS.CLICK_ANCHOR, {
          button_name: detail.buttonName || '',
          module_name: detail.moduleName || 'pop-up_anchor navigation'
        });
      });

      document.addEventListener('eurus:cart:items-changed', () => {
        if (!this.xcottonCartSnapshot) {
          this.refreshXcottonCartSnapshot();
          return;
        }
        this.trackXcottonShippingProtectionCartChange(Promise.resolve(this.xcottonCartSnapshot), null, this.xcottonCartChangeToken);
      });
    }

     /**
     * 全局点击事件分发器。
     * 按业务优先级依次尝试处理：checkout、购物车工具、头部、底部、banner、详情、tab、商品、普通按钮。
     * 每个 handler 返回 true 表示已经命中埋点，后面的泛化逻辑不再继续匹配。
     */
    handleClick(event) {
      const target = event.target;
      if (!target || !(target instanceof Element)) return;
      if (this.isThirdPartyMarketingElement(target)) return;
      if (target.closest('[data-collar-go-3-nav-link]')) return;

      if (this.handleCheckoutClick(target)) return;
      if (this.handleXcottonShippingProtectionClick(target)) return;
      if (this.handleCartUtilityClick(target)) return;
      if (this.handleHeaderClick(target)) return;
      if (this.handleFooterClick(target)) return;
      if (this.handleBannerClick(target)) return;
      if (this.handleDetailsClick(target)) return;
      if (this.handleTabClick(target)) return;
      if (this.handleProductSelectClick(target)) return;
      if (this.handleQuizButtonClick(target)) return;
      if (this.handleGenericButtonClick(target)) return;
    }

    /**
     * 处理顶部区域点击。
     * 只认 #x-header-container，功能入口上报 click_function，普通导航上报 click_navigation。
     */
    handleHeaderClick(target) {
      const header = target.closest('#x-header-container');
      if (!header) return false;
      if (this.isCartAreaElement(target)) return false;

      const functionControl = target.closest('a, button');
      if (functionControl && this.isHeaderFunction(functionControl)) {
        const buttonName = this.getHeaderFunctionName(functionControl);
        this.pushEvent(GA4_EVENTS.CLICK_FUNCTION, {
          module_name: 'top function',
          button_name: buttonName
        });

        if (buttonName === 'cart') {
          setTimeout(() => {
            const drawer = document.getElementById('CartDrawer');
            if (drawer && this.isCartDrawerOpen(drawer)) this.trackCartDrawerOpen();
          }, 500);
        }
        return true;
      }

      const link = target.closest('a[href]');
      if (!link) return false;
      const name = this.getButtonName(link);
      if (!name) return false;

      const isSecond = Boolean(link.closest('.toggle-menu, .toggle-menu-sub, .mega-sub-nav'));
      const firstNavigation = isSecond ? this.getFirstNavigationName(link) : name;
      const params = {
        module_name: isSecond ? 'top second navigation' : 'top first navigation',
        first_navigation: firstNavigation || name
      };
      if (isSecond) params.second_navigation = name;
      this.pushEvent(GA4_EVENTS.CLICK_NAVIGATION, params);
      return true;
    }

    /**
     * 判断 header 内点击元素是否属于功能入口。
     * search/cart/account/logo 这类入口不归为导航，需要按 click_function 上报。
     */
    isHeaderFunction(control) {
      const id = control.id || '';
      const href = control.getAttribute('href') || '';
      const label = `${id} ${control.className || ''} ${control.getAttribute('aria-label') || ''} ${href}`.toLowerCase();
      return label.includes('search') ||
        label.includes('cart') ||
        label.includes('account') ||
        label.includes('login') ||
        label.includes('logo') ||
        control.closest('[id*="SearchOpen"], [id*="cart-icon"], [class*="logo"]');
    }

    /**
     * 从 header 功能入口中提取 button_name。
     * 优先根据 id/class/aria-label/href 识别 search/cart/account/logo，识别不到再退回按钮文本。
     */
    getHeaderFunctionName(control) {
      const label = `${control.id || ''} ${control.className || ''} ${control.getAttribute('aria-label') || ''} ${control.getAttribute('href') || ''}`.toLowerCase();
      if (label.includes('search')) return 'search';
      if (label.includes('cart')) return 'cart';
      if (label.includes('account') || label.includes('login')) return 'account';
      if (label.includes('logo') || control.closest('[class*="logo"]')) return 'logo';
      return this.getButtonName(control) || 'top function';
    }

    /**
     * 获取二级导航所属的一级导航名。
     * 只从 header 一级菜单 li 中取直接标题，避免从当前二级菜单 li 误取自身链接。
     */
    getFirstNavigationName(link) {
      const topMenu = link.closest('#x-header-container li.tree-menu, #x-header-container li.tabbed_menu, #x-header-container li.banner_menu, #x-header-container li.blog_menu, #x-header-container li.contact_menu, #x-header-container li.simple_menu');
      const topLink = topMenu?.querySelector(':scope > .tree-menu-item > a[href], :scope > .tabbed_menu_item a[href], :scope > .simple_menu_item a[href], :scope > .blog_menu_item a[href], :scope > a[href]');
      const topName = this.getButtonName(topLink);
      if (topName) return topName;
      return '';
    }

    /**
     * 处理底部区域点击。
     * 只认 #x-footer-container；社交链接上报 follow_us，其余底部链接按 bottom navigation 上报。
     */
    handleFooterClick(target) {
      const footer = target.closest('#x-footer-container');
      if (!footer) return false;

      const link = target.closest('a[href]');
      if (!link) return false;
      const href = link.getAttribute('href') || '';
      const buttonName = this.getButtonName(link);
      if (!buttonName) return false;

      if (this.isSocialLink(href)) {
        this.pushEvent(GA4_EVENTS.FOLLOW_US, { button_name: this.getSocialName(href) || buttonName });
      } else {
        this.pushEvent(GA4_EVENTS.CLICK_NAVIGATION, {
          module_name: 'bottom navigation',
          first_navigation: this.getFooterGroupName(link) || buttonName,
          second_navigation: buttonName
        });
      }
      return true;
    }

    /**
     * 处理首页 banner 点击。
     * 只认 Liquid 显式输出的 data-gtm-view-banner 元素；轮播箭头、分页点不在 slide 内，因此不会误报 click_banner。
     */
    handleBannerClick(target) {
      const banner = target.closest('[data-gtm-view-banner="true"]');
      if (!banner || !this.isHomePage()) return false;
      const control = target.closest('a, button');
      if (!control) return false;
      if (this.shouldIgnoreThemeClick(control)) return false;

      this.pushEvent(GA4_EVENTS.CLICK_BANNER, {
        module_name: banner.dataset.gtmModuleName || 'top banner',
        title: this.getBannerTitle(banner),
        position: this.getBannerPosition(banner),
        button_name: this.getButtonName(control) || 'a link'
      });
      return true;
    }

    /**
     * 处理折叠内容或 FAQ 点击。
     * 优先识别产品详情页 liquid 标记的 data-gtm-details-toggle，按当前内容可见性反推本次点击后的 expand/fold；
     * 其他原生 details/summary 继续兼容旧逻辑，FAQ 文案类入口归入 button_click。
     */
    handleDetailsClick(target) {
      if (target.closest('[data-gtm-details-custom="true"]')) return false;

      const detailsToggle = target.closest('[data-gtm-details-toggle]');
      if (detailsToggle && !detailsToggle.closest('#x-header-container, #x-footer-container')) {
        const detailsItem = detailsToggle.closest('[data-gtm-details-item]');
        const content = detailsItem?.querySelector('[data-gtm-details-content]');
        const title = detailsToggle.dataset.gtmDetailsTitle || this.getButtonName(detailsToggle) || this.getElementTitle(detailsToggle);
        if (!title) return false;

        this.pushEvent(GA4_EVENTS.DETAILS_CLICK, {
          title,
          module_name: detailsToggle.dataset.gtmModuleName || 'product details',
          status: content && this.isElementVisible(content) ? 'fold' : 'expand'
        });
        return true;
      }

      const summary = target.closest('summary, .faq-question, [aria-expanded]');
      if (!summary || summary.closest('#x-header-container, #x-footer-container')) return false;

      const details = summary.closest('details');
      const title = this.getButtonName(summary) || this.getElementTitle(summary);
      if (!title) return false;

      const willOpen = details ? !details.open : summary.getAttribute('aria-expanded') !== 'true';
      const eventName = summary.closest('.faq, .collar-faq') || title.toLowerCase().includes('faq') ? GA4_EVENTS.BUTTON_CLICK : GA4_EVENTS.DETAILS_CLICK;
      if (eventName === GA4_EVENTS.DETAILS_CLICK) {
        this.pushEvent(GA4_EVENTS.DETAILS_CLICK, {
          title,
          module_name: this.getModuleName(summary, 'Product Description'),
          status: willOpen ? 'expand' : 'fold'
        });
      } else {
        this.pushEvent(GA4_EVENTS.BUTTON_CLICK, {
          button_name: title,
          module_name: this.getModuleName(summary, 'Frequently Asked Questions')
        });
      }
      return true;
    }

    /**
     * 处理 tab 切换点击。
     * 从 data-tabtitle、role=tab 或 tab 文本中提取 button_name，上报 tab_click。
     */
    handleTabClick(target) {
      const tab = target.closest('.tab-item, .faq-tab, [role="tab"], [data-tabtitle]');
      if (!tab) return false;
      const buttonName = tab.dataset.tabtitle || this.getButtonName(tab);
      if (!buttonName) return false;
      const moduleName = tab.closest('[data-gtm-module-name]')?.dataset?.gtmModuleName ||
        this.getModuleName(tab, 'Frequently Asked Questions');

      this.pushEvent(GA4_EVENTS.TAB_CLICK, {
        button_name: buttonName,
        module_name: moduleName
      });
      return true;
    }

    /**
     * 处理商品点击并上报 select_item。
     * 商品链接点击沿用原逻辑；GemPages 主图不是 a 标签，但会由组件自己跳转，所以这里额外识别 gp-product-images。
     * 对有内联产品 JSON 的 GemPages 卡片会同步生成 items，避免点击跳转后异步请求来不及完成。
     */
    handleProductSelectClick(target) {
      const link = target.closest('a[href*="/products/"]');
      const gemPagesImageClick = link ? null : this.getGemPagesProductImageClick(target);
      const sourceElement = link || gemPagesImageClick?.element;
      if (!sourceElement) return false;
      if (this.shouldIgnoreThemeClick(sourceElement)) return false;
      if (sourceElement.closest('#x-header-container, #x-footer-container') && !sourceElement.closest('#CartDrawer')) return false;

      const moduleName = this.getProductModuleName(sourceElement);
      const itemListName = this.getItemListName(sourceElement, moduleName);
      const itemListId = this.slugify(itemListName);
      const buttonName = link ? (this.getButtonName(link) || 'a link') : 'product image';
      const productUrl = link ? (link.getAttribute('href') || '') : (gemPagesImageClick.productUrl || '');
      const datasetItem = link ? this.buildItemFromTrackingDataset(link, {
        itemListId,
        itemListName
      }) : null;

      if (datasetItem) {
        this.pushEvent(GA4_EVENTS.SELECT_ITEM, {
          item_list_name: itemListName,
          item_list_id: itemListId,
          currency: this.getCurrency(),
          button_name: buttonName,
          module_name: moduleName,
          items: [datasetItem]
        });
        return true;
      }

      if (gemPagesImageClick?.product) {
        const item = this.buildItemFromProduct(gemPagesImageClick.product, {
          variantId: this.getVariantIdFromUrl(productUrl),
          index: this.getElementPosition(gemPagesImageClick.card || sourceElement),
          itemListId,
          itemListName,
          quantity: 1
        });

        this.pushEvent(GA4_EVENTS.SELECT_ITEM, {
          item_list_name: itemListName,
          item_list_id: itemListId,
          currency: this.getCurrency(),
          button_name: buttonName,
          module_name: moduleName,
          items: [item]
        });
        return true;
      }

      this.fetchProductInfoFromUrl(productUrl).then((product) => {
        if (!product) return;
        const item = this.buildItemFromProduct(product, {
          variantId: this.getVariantIdFromUrl(productUrl),
          index: this.getElementPosition(sourceElement),
          itemListId,
          itemListName,
          quantity: 1
        });

        this.pushEvent(GA4_EVENTS.SELECT_ITEM, {
          item_list_name: itemListName,
          item_list_id: itemListId,
          currency: this.getCurrency(),
          button_name: buttonName,
          module_name: moduleName,
          items: [item]
        });
      });
      return true;
    }

    /**
     * 识别 GemPages 商品主图点击。
     * GemPages 的商品图片跳转由自定义组件内部处理，DOM 上通常没有 a 标签；这里只在 gp-data 明确有 productUrl，
     * 且点击发生在带 data-event-click=true 的图片区域时命中，避免把缩略图切换、按钮和表单误当成商品点击。
     */
    getGemPagesProductImageClick(target) {
      const productImages = target.closest('gp-product-images[gp-data]');
      if (!productImages) return null;
      if (target.closest('a, button, input, select, textarea')) return null;

      const clickableImage = target.closest('[data-event-click="true"], .gp-featured-image-wrapper, img.featured-image-only');
      if (!clickableImage || !productImages.contains(clickableImage)) return null;

      const data = this.getGemPagesProductImagesData(productImages);
      if (!data.productUrl) return null;
      if (!this.isGemPagesProductImageLinked(data, clickableImage)) return null;

      return {
        element: productImages,
        card: productImages.closest('gp-row[id], [data-same-height-subgrid-container], [data-product-card-tracked]') || productImages,
        productUrl: data.productUrl,
        product: data.product
      };
    }

    /**
     * 读取 GemPages gp-product-images 的内联配置。
     * gp-data 里包含 productUrl 和完整 product JSON；读取成功后顺手写入 productCache，后续同商品点击可少一次请求。
     */
    getGemPagesProductImagesData(productImages) {
      const data = this.parseJsonAttribute(productImages, 'gp-data') || {};
      const productUrl = data.productUrl || '';
      const product = data.product || null;
      const handle = this.getProductHandleFromUrl(productUrl);
      if (handle && product) this.productCache.set(handle, product);

      return { productUrl, product, setting: data.setting || {} };
    }

    /**
     * 判断 GemPages 图片点击是否配置为商品跳转。
     * 优先信任图片区域的 data-event-click=true；同时兼容 gp-data 内的 product-link 设置，防止非跳转图片被误报。
     */
    isGemPagesProductImageLinked(data, clickableImage) {
      if (clickableImage?.closest?.('[data-event-click="true"]')) return true;
      const setting = data.setting || {};
      const linkModes = [
        setting.ftClickOpenLightBox?.desktop,
        setting.ftClickOpenLightBox?.tablet,
        setting.ftClickOpenLightBox?.mobile,
        setting.clickOpenLightBox?.desktop,
        setting.clickOpenLightBox?.tablet,
        setting.clickOpenLightBox?.mobile
      ];
      return linkModes.includes('product-link');
    }

    /**
     * 处理 Quiz 弹窗相关按钮点击。
     * 入口按钮用 data-gtm-quiz-button="trigger" 显式标记；弹窗内只作用于 .quiz-modal-container，
     * 命中后直接返回 true，避免落到通用 button_click 逻辑导致 module_name 和 button_name 被误判。
     */
    handleQuizButtonClick(target) {
      const trigger = target.closest('[data-gtm-quiz-button="trigger"]');
      if (trigger) {
        const buttonName = this.getButtonName(trigger);
        if (!buttonName) return false;

        this.pushEvent(GA4_EVENTS.BUTTON_CLICK, {
          button_name: buttonName,
          module_name: QUIZ_MODULE_START
        });
        return true;
      }

      const modal = target.closest('.quiz-modal-container');
      if (!modal) return false;

      const control = target.closest('button, a, input[type="button"], input[type="submit"]');
      if (!control || !modal.contains(control)) return false;
      if (this.shouldIgnoreThemeClick(control)) return false;

      const buttonName = this.getQuizButtonName(control, modal);
      if (!buttonName) return false;

      this.pushEvent(GA4_EVENTS.BUTTON_CLICK, {
        button_name: buttonName,
        module_name: this.getQuizModuleName(modal)
      });
      return true;
    }

    /**
     * 生成 Quiz 内部 button_name。
     * 选项类按钮固定追加 _Select；底部/顶部动作按钮会拼接当前步骤已选选项，
     * 没有选项的步骤只返回按钮自身文案，例如 Estimate & Continue。
     */
    getQuizButtonName(control, modal) {
      const option = control.closest('.quiz-modal-option');
      if (option && modal.contains(option)) {
        const optionText = this.getQuizOptionText(option);
        return optionText ? `${optionText}_Select` : '';
      }

      const optionLikeControl = control.closest('.quiz-modal-breed-tag, .quiz-modal-unit-btn');
      if (optionLikeControl && modal.contains(optionLikeControl)) {
        const optionText = this.getQuizActionButtonName(optionLikeControl);
        return optionText ? `${optionText}_Select` : '';
      }

      const actionName = this.getQuizActionButtonName(control);
      if (!actionName) return '';

      const selectedOptionText = this.getQuizSelectedOptionText(modal);
      return selectedOptionText ? `${selectedOptionText}_${actionName}` : actionName;
    }

    /**
     * 读取 Quiz 动作按钮名称。
     * 对 Add to Cart / Estimate & Continue 这类含多个状态 span 的按钮，只取当前可见文案，
     * 避免把 Adding、Added、Estimating 等隐藏状态一起拼进 button_name。
     */
    getQuizActionButtonName(control) {
      if (!control) return '';
      if (control.classList?.contains('quiz-modal-close')) return control.getAttribute('aria-label') || 'Close';
      if (control.classList?.contains('quiz-modal-btn-back')) return 'Back';
      if (control.classList?.contains('quiz-modal-btn-restart')) return 'Start Over';
      if (control.classList?.contains('quiz-modal-btn-next') || control.classList?.contains('quiz-modal-btn-cta')) {
        const visibleLabel = this.getVisibleQuizButtonLabel(control);
        if (visibleLabel) return visibleLabel;
      }
      return this.getButtonName(control);
    }

    /**
     * 从 Quiz 按钮内部挑选当前可见的短文案。
     * 只看 span 节点是为了避开 SVG、spinner 以及同一个按钮内的其他隐藏状态。
     */
    getVisibleQuizButtonLabel(control) {
      const spans = Array.from(control.querySelectorAll('span'));
      const visibleSpan = spans.find((span) => !span.classList.contains('is-hidden') && this.isElementVisible(span) && this.getText(span));
      return this.getText(visibleSpan);
    }

    /**
     * 读取当前步骤已选择的 Quiz 选项。
     * 这里只读取 .quiz-modal-option.selected，dog profile 这类表单步骤不参与前缀拼接，
     * 因此 Estimate & Continue 会按需求只传按钮自身文案。
     */
    getQuizSelectedOptionText(modal) {
      const panel = this.getVisibleQuizPanel(modal);
      const selectedOption = panel?.querySelector('.quiz-modal-option.selected');
      return this.getQuizOptionText(selectedOption);
    }

    /**
     * 读取 Quiz 选项的主 label。
     * 优先取 .quiz-modal-option-label，避免把 hint 说明文案拼进 button_name。
     */
    getQuizOptionText(option) {
      if (!option) return '';
      return this.getText(option.querySelector('.quiz-modal-option-label')) || this.getButtonName(option);
    }

    /**
     * 生成 Quiz 的 module_name。
     * 固定前缀为 will it work for me_start；当前可见步骤有标题时追加步骤标题，
     * 结果页没有 step title 时读取可见的 .quiz-modal-result-title。
     */
    getQuizModuleName(modal) {
      const panel = this.getVisibleQuizPanel(modal);
      const stepTitle = this.getText(panel?.querySelector('.quiz-modal-step-title'));
      const visibleResultTitle = Array.from(panel?.querySelectorAll('.quiz-modal-result-title') || [])
        .find((title) => this.isElementVisible(title));
      const resultTitle = this.getText(visibleResultTitle);
      const stepText = stepTitle || resultTitle;
      return stepText ? `${QUIZ_MODULE_START}_${stepText}` : QUIZ_MODULE_START;
    }

    /**
     * 找到 Quiz 当前可见步骤面板。
     * Alpine 的 x-show 会把非当前步骤设为 display:none，所以这里以可见性作为唯一状态来源。
     */
    getVisibleQuizPanel(modal) {
      const panels = Array.from(modal?.querySelectorAll('.quiz-modal-step-panel') || []);
      return panels.find((panel) => this.isElementVisible(panel)) || null;
    }

    /**
     * 处理普通正文按钮点击。
     * 会排除 header/footer、购物车、商品卡片、加购按钮、tab 和 details，避免和更精确的埋点重复。
     */
    handleGenericButtonClick(target) {
      const control = target.closest('a, button, input[type="button"], input[type="submit"]');
      if (!control) return false;
      if (this.shouldIgnoreThemeClick(control)) return false;
      if (control.closest('#x-header-container, #x-footer-container, .cart-item, #CartDrawer, #main-cart-items')) return false;
      if (control.closest('.card-product') || this.isAddToCartControl(control)) return false;
      if (control.closest('.tab-item, details, summary')) return false;

      const buttonName = this.getButtonName(control);
      if (!buttonName || buttonName.length > 120) return false;

      this.pushEvent(GA4_EVENTS.BUTTON_CLICK, {
        button_name: buttonName,
        module_name: this.getModuleName(control, 'unknown module')
      });
      return true;
    }

    /**
     * 判断当前点击是否交给 GTM 自己处理。
     * 有些按钮只需要输出稳定 id 给 GTM 配置触发器，主题脚本不再额外推送 click_banner/button_click/select_item。
     */
    shouldIgnoreThemeClick(control) {
      return Boolean(control?.closest?.('[data-gtm-theme-click-ignore="true"]'));
    }

    /**
     * 判断点击是否来自购物车区域。
     * 侧栏购物车被异步渲染在 header 容器内，所以 header click_function 需要显式排除这些节点。
     */
    isCartAreaElement(element) {
      return Boolean(element?.closest?.('#CartDrawer, #CartDrawer-Overlay, #main-cart-items, #main-cart-footer'));
    }

    /**
     * 判断点击是否来自第三方营销组件。
     * Klaviyo 弹窗由第三方脚本注入，主题侧不再对其 Join Now 等按钮做点击埋点。
     */
    isThirdPartyMarketingElement(element) {
      let current = element;
      while (current && current !== document.documentElement) {
        const label = `${current.id || ''} ${current.getAttribute?.('class') || ''} ${current.getAttribute?.('data-testid') || ''}`.toLowerCase();
        if (label.includes('klaviyo')) return true;
        current = current.parentElement;
      }
      return false;
    }

    /**
     * 处理购物车 checkout 按钮点击。
     * 只上报需求中的 checkout 点击事件，不处理结账页后续流程。
     */
    handleCheckoutClick(target) {
      const button = target.closest('#checkout-submit, [name="checkout"], a[href="/checkout"], button[name="checkout"]');
      if (!button) return false;
      this.trackCheckout(button);
      return true;
    }

    /**
     * 组装并上报 checkout 事件。
     * 只有按钮位于 #CartDrawer 时才传 module_name=pop-up cart；购物车页 checkout 按需求不传 module_name。
     */
    trackCheckout(button) {
      const params = {
        button_name: this.getButtonName(button) || 'Checkout'
      };
      if (button?.closest?.('#CartDrawer')) {
        params.module_name = 'pop-up cart';
      }
      this.pushEvent(GA4_EVENTS.CHECKOUT, params);
    }

    /**
     * 处理 XCotton 注入的 shipping protect 开关。
     * 插件用 div#xcotton-switch 和 checked class 表示状态，不是原生 checkbox；
     * 点击只作为“开始检查购物车真实变化”的信号，最终是否上报取决于 /cart.js 是否真的增加或移除了保护商品。
     */
    handleXcottonShippingProtectionClick(target) {
      const switchEl = this.getXcottonShippingProtectionSwitch(target);
      if (!switchEl) return false;

      const expectedEventName = switchEl.classList.contains('checked') ? GA4_EVENTS.REMOVE_FROM_CART : GA4_EVENTS.ADD_TO_CART;
      const token = this.xcottonCartChangeToken + 1;
      this.xcottonCartChangeToken = token;
      const previousCartPromise = this.getXcottonPreviousCartSnapshot();
      this.trackXcottonShippingProtectionCartChange(previousCartPromise, switchEl, token, expectedEventName);
      return true;
    }

    /**
     * 从点击目标定位 XCotton 开关。
     * 命中范围只限 CartDrawer 内，并且必须找到明确的 #xcotton-switch，避免误伤普通购物车按钮。
     */
    getXcottonShippingProtectionSwitch(target) {
      const cartDrawer = target.closest('#CartDrawer');
      if (!cartDrawer) return null;

      const directSwitch = target.closest('#xcotton-switch');
      if (directSwitch && cartDrawer.contains(directSwitch)) return directSwitch;

      let current = target;
      let depth = 0;
      while (current && current !== cartDrawer && depth < 6) {
        if (this.isXcottonShippingProtectionContainer(current)) {
          const nestedSwitch = current.querySelector?.('#xcotton-switch') || cartDrawer.querySelector('#xcotton-switch');
          if (nestedSwitch && cartDrawer.contains(nestedSwitch)) return nestedSwitch;
        }
        current = current.parentElement;
        depth += 1;
      }
      return null;
    }

    /**
     * 判断某个局部 DOM 是否属于 XCotton Shipping Protection。
     * 非直接点击 switch 时才使用该判断；文本判断限制长度，避免整个 cart drawer/footer 被误识别。
     */
    isXcottonShippingProtectionContainer(element) {
      const attributes = [
        element.id,
        element.getAttribute?.('class'),
        element.getAttribute?.('aria-label'),
        element.getAttribute?.('data-testid'),
        element.getAttribute?.('data-name')
      ].filter(Boolean).join(' ').toLowerCase();

      if (attributes.includes('xcotton')) return true;
      if (attributes.includes('protection')) return true;
      if (attributes.includes('shipping') && attributes.includes('protect')) return true;

      const text = this.getText(element).toLowerCase().replace(/\s+/g, ' ').trim();
      if (!text || text.length > 160) return false;
      return text.includes('shipping protection') || (text.includes('shipping') && text.includes('protect')) || (text.includes('protect') && text.includes('order'));
    }

    /**
     * 记录 XCotton 开关节点出现时的购物车快照。
     * 这份快照用于后续点击时做真实 cart diff，避免临时请求 cart.js 时已经被插件改完导致拿不到“变化前”状态。
     */
    observeXcottonShippingProtection() {
      const switchEl = document.querySelector('#CartDrawer #xcotton-switch');
      if (!switchEl) return;
      if (switchEl === this.observedXcottonSwitch) return;
      this.observedXcottonSwitch = switchEl;
      this.refreshXcottonCartSnapshot();
    }

    /**
     * 获取 XCotton 点击前的购物车快照。
     * 优先使用最近一次已记录的快照；没有快照时才请求 cart.js。
     */
    getXcottonPreviousCartSnapshot() {
      if (this.xcottonCartSnapshot) return Promise.resolve(this.xcottonCartSnapshot);
      return this.fetchCart();
    }

    /**
     * 刷新 XCotton 使用的购物车快照。
     * 失败时保持旧快照，不影响页面交互。
     */
    refreshXcottonCartSnapshot() {
      this.fetchCart().then((cart) => {
        if (cart) this.xcottonCartSnapshot = cart;
      });
    }

    /**
     * 轮询 cart.js 并对比 Shipping Protection 购物车行。
     * 只有真实 cart item 数量发生变化时才上报；没有变化就继续等待，最多检查 4 秒。
     */
    trackXcottonShippingProtectionCartChange(previousCartPromise, switchEl, token, expectedEventName = '', attempt = 0) {
      const delay = attempt === 0 ? 450 : 700;
      window.setTimeout(async () => {
        if (token && token !== this.xcottonCartChangeToken) return;

        const previousCart = await previousCartPromise;
        const currentCart = await this.fetchCart();
        if (!currentCart) return;

        const tracked = this.pushXcottonShippingProtectionCartDiff(previousCart, currentCart, switchEl, expectedEventName);
        this.xcottonCartSnapshot = currentCart;
        if (tracked || attempt >= 5) return;

        this.trackXcottonShippingProtectionCartChange(Promise.resolve(previousCart), switchEl, token, expectedEventName, attempt + 1);
      }, delay);
    }

    /**
     * 根据两次 cart.js 的差异判断 Shipping Protection 是新增还是移除。
     * add 使用当前购物车行，remove 使用旧购物车行，这样移除后仍能拿到价格。
     */
    pushXcottonShippingProtectionCartDiff(previousCart, currentCart, switchEl, expectedEventName = '') {
      const previousItem = this.getXcottonShippingProtectionCartItem(previousCart);
      const currentItem = this.getXcottonShippingProtectionCartItem(currentCart);
      const previousQuantity = this.toNumber(previousItem?.quantity, 0) || 0;
      const currentQuantity = this.toNumber(currentItem?.quantity, 0) || 0;
      const delta = currentQuantity - previousQuantity;
      if (!delta) {
        if (expectedEventName === GA4_EVENTS.ADD_TO_CART && currentItem) {
          this.pushXcottonShippingProtectionEvent(expectedEventName, currentItem, currentQuantity || 1, currentCart, switchEl);
          return true;
        }
        return false;
      }

      const eventName = delta > 0 ? GA4_EVENTS.ADD_TO_CART : GA4_EVENTS.REMOVE_FROM_CART;
      const quantity = Math.abs(delta);
      const sourceItem = delta > 0 ? currentItem : previousItem;
      this.pushXcottonShippingProtectionEvent(eventName, sourceItem, quantity, currentCart || previousCart, switchEl);
      return true;
    }

    /**
     * 从 cart.js 中识别 XCotton 的 Shipping Protection 商品行。
     * 这里按商品标题/类型/URL/属性做文本匹配，兼容第三方插件返回字段不完全一致的情况。
     */
    getXcottonShippingProtectionCartItem(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      return items.find((item) => this.isXcottonShippingProtectionCartItem(item)) || null;
    }

    /**
     * 判断 cart item 是否是 Shipping Protection。
     * 优先识别 xcotton，其次识别 shipping protection/protect order 等常见插件文案。
     */
    isXcottonShippingProtectionCartItem(item) {
      const properties = item?.properties && typeof item.properties === 'object' ? Object.entries(item.properties).flat().join(' ') : '';
      const label = [
        item?.handle,
        item?.url,
        item?.vendor,
        item?.product_type,
        item?.product_title,
        item?.title,
        item?.variant_title,
        properties
      ].filter(Boolean).join(' ').toLowerCase();

      if (!label) return false;
      if (label.includes('xcotton')) return true;
      if (label.includes('shipping protection')) return true;
      if (label.includes('shipping protect')) return true;
      return label.includes('protect') && (label.includes('order') || label.includes('package') || label.includes('shipment'));
    }

    /**
     * 上报 Shipping Protection 的 add_to_cart/remove_from_cart。
     * item_id/item_name 维持需求中的固定值，金额优先取真实 cart item，取不到再解析开关附近文案。
     */
    pushXcottonShippingProtectionEvent(eventName, cartItem, quantity, cart, switchEl) {
      const price = this.getXcottonShippingProtectionItemPrice(cartItem, switchEl);
      const trackKey = `${eventName}:${cartItem?.key || cartItem?.variant_id || 'xcotton'}:${quantity}:${price}`;
      const now = Date.now();
      if (this.lastXcottonCartTrackKey === trackKey && now - this.lastXcottonCartTrackedAt < 1200) return;
      this.lastXcottonCartTrackKey = trackKey;
      this.lastXcottonCartTrackedAt = now;

      const item = {
        item_id: 'xcotton_shipping_protection',
        item_name: 'Shipping Protection',
        coupon: '',
        discount: 0,
        index: '',
        item_list_id: 'cart',
        item_list_name: 'Shopping Cart',
        item_category: 'Shipping Protection',
        item_variant: cartItem?.variant_title || '',
        item_brand: BRAND_NAME,
        price,
        quantity
      };

      this.pushEvent(eventName, {
        currency: this.getCurrency(cart),
        value: this.roundMoney(price * quantity),
        button_name: 'Shipping Protection',
        module_name: 'pop-up cart',
        items: [item]
      });
    }

    /**
     * 获取 Shipping Protection 单价。
     * cart.js 中的金额是 cents；插件刚新增但 cart item 未完整返回时，才从 switch 附近文案兜底解析。
     */
    getXcottonShippingProtectionItemPrice(cartItem, switchEl) {
      const cartPrice = cartItem ? this.centsToMoney(cartItem.final_price || cartItem.price || 0) : 0;
      if (cartPrice) return cartPrice;
      return this.getXcottonShippingProtectionPrice(switchEl);
    }

    /**
     * 从 XCotton 开关附近文案解析保护费用。
     * 只向上查找少量父级，避免误抓到整个侧栏购物车里的商品价格。
     */
    getXcottonShippingProtectionPrice(switchEl) {
      let current = switchEl;
      let depth = 0;
      while (current && current.id !== 'CartDrawer' && depth < 5) {
        const price = this.parseMoneyFromText(this.getText(current));
        if (price) return price;
        current = current.parentElement;
        depth += 1;
      }
      return 0;
    }

    /**
     * 从普通文案中解析货币金额。
     * 支持 "$2.99"、"USD 2.99"、"2.99 USD" 等常见格式；无法识别时返回 0。
     */
    parseMoneyFromText(text) {
      const normalized = String(text || '').replace(/\u00a0/g, ' ');
      const amountMatch = normalized.match(/(?:[$€£¥]|USD|CAD|AUD|EUR|GBP)\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i) ||
        normalized.match(/([0-9][0-9,]*(?:\.[0-9]{1,2})?)\s*(?:USD|CAD|AUD|EUR|GBP)/i);
      if (!amountMatch) return 0;
      return this.roundMoney(amountMatch[1].replace(/,/g, ''));
    }

    /**
     * 处理购物车内的辅助按钮。
     * 目前覆盖 order note 入口、shipping calculate 入口；真正的 calculate complete 由运费脚本成功后发自定义事件。
     */
    handleCartUtilityClick(target) {
      const cartRoot = target.closest('#CartDrawer, #main-cart-items, #main-cart-footer');
      if (!cartRoot) return false;

      const noteButton = target.closest('[aria-controls*="note"], button');
      if (noteButton && this.getButtonName(noteButton).toLowerCase().includes('note')) {
        this.pushEvent(GA4_EVENTS.BUTTON_CLICK, {
          button_name: 'order note',
          module_name: `${this.getCartModuleBaseName()}_order note`
        });
        return true;
      }

      const shippingButton = target.closest('button, a');
      const shippingButtonName = this.getButtonName(shippingButton);
      const isShippingEntry = shippingButtonName.toLowerCase().includes('shipping');
      if (shippingButton && isShippingEntry) {
        this.pushEvent(GA4_EVENTS.BUTTON_CLICK, {
          button_name: shippingButtonName,
          module_name: `${this.getCartModuleBaseName()}_shipping calculate_start`
        });
        return true;
      }

      return false;
    }

    /**
     * 处理加购成功后的自定义事件。
     * 优先使用接口返回的 cart item 数据生成 items；如果接口没有返回商品信息，再根据表单或当前 PDP 数据补齐。
     */
    async handleProductAdded(detail) {
      const formData = detail.formData;
      const productForm = detail.productForm || detail.productCard || detail.button?.closest?.('form, .card-product, [data-product-id]');
      const moduleName = this.getAddToCartModuleName(productForm);
      const itemListName = moduleName === 'pop-up cart' ? 'Shopping Cart' : moduleName;
      const itemListId = this.slugify(itemListName);
      const responseItems = this.getAddedResponseItems(detail);
      const items = [];

      if (responseItems.length) {
        responseItems.forEach((responseItem, index) => {
          items.push(this.buildItemFromCartItem(responseItem, {
            quantity: this.toNumber(responseItem.quantity || detail.quantity, 1) || 1,
            itemListId,
            itemListName,
            index
          }));
        });
      } else {
        const quantity = this.toNumber(formData?.get('quantity') || detail.quantity || detail.response?.quantity, 1) || 1;
        const variantId = String(formData?.get('id') || detail.variantId || detail.response?.variant_id || detail.response?.id || '');
        const product = await this.getProductForAdd(productForm, variantId);
        if (!product) return;

        items.push(this.buildItemFromProduct(product, {
          variantId,
          quantity,
          itemListId,
          itemListName,
          index: ''
        }));
      }

      if (!items.length) return;

      this.pushEvent(GA4_EVENTS.ADD_TO_CART, {
        currency: this.getCurrency(),
        value: this.roundMoney(items.reduce((total, item) => total + ((item.price || 0) * (item.quantity || 1)), 0)),
        button_name: this.getAddToCartButtonName(productForm, detail.button),
        module_name: moduleName,
        items
      });
    }

    /**
     * 处理购物车数量变化成功后的事件。
     * 根据 delta 正负决定上报 add_to_cart 或 remove_from_cart，并用更新前的 oldItem 生成商品参数。
     */
    handleCartQuantityChanged(detail) {
      const oldItem = detail.oldItem;
      if (!oldItem || !detail.delta) return;
      if (detail.cart) this.xcottonCartSnapshot = detail.cart;

      const eventName = detail.delta > 0 ? GA4_EVENTS.ADD_TO_CART : GA4_EVENTS.REMOVE_FROM_CART;
      const quantity = Math.abs(detail.delta);
      const moduleName = this.getCartModuleBaseName();
      const item = this.buildItemFromCartItem(oldItem, {
        quantity,
        index: Math.max((detail.line || 1) - 1, 0),
        itemListId: 'cart',
        itemListName: 'Shopping Cart'
      });

      this.pushEvent(eventName, {
        currency: this.getCurrency(detail.cart),
        value: this.roundMoney((item.price || 0) * quantity),
        button_name: detail.button_name || (eventName === GA4_EVENTS.ADD_TO_CART ? 'input' : 'remove'),
        module_name: moduleName,
        items: [item]
      });
    }

    /**
     * 处理 PDP 商品详情曝光。
     * 页面初始加载和变体切换都会调用；lastProductViewVariantId 用于避免同一变体重复上报。
     */
    trackProductView() {
      if (!this.isProductPage() || !window.__SHOPIFY_PRODUCT__) return;
      const product = window.__SHOPIFY_PRODUCT__;
      const variantId = this.getCurrentVariantId(product);
      if (variantId && this.lastProductViewVariantId === String(variantId)) return;
      this.lastProductViewVariantId = String(variantId || product.id || '');

      const item = this.buildItemFromProduct(product, {
        variantId,
        quantity: 1,
        itemListId: '',
        itemListName: '',
        index: ''
      });

      this.pushEvent(GA4_EVENTS.VIEW_ITEM, {
        currency: this.getCurrency(),
        value: item.price || 0,
        items: [item]
      });
    }

    /**
     * 处理购物车页初始曝光。
     * 进入 /cart 时同时上报 view_cart 和购物车商品列表 view_item_list。
     */
    trackCartPageView() {
      if (!this.isCartPage()) return;
      this.trackCartView('cart page');
      this.trackCartItemList('cart page');
    }

    /**
     * 上报购物车内容曝光 view_cart。
     * 会读取 /cart.js 获取当前购物车；800ms 内去重，避免侧栏 class/style 连续变化导致重复上报。
     */
    trackCartView(moduleName) {
      const now = Date.now();
      if (now - this.lastCartViewAt < 800) return;
      this.lastCartViewAt = now;

      this.fetchCart().then((cart) => {
        if (cart) this.xcottonCartSnapshot = cart;
        const items = Array.isArray(cart?.items) ? cart.items : [];
        this.pushEvent(GA4_EVENTS.VIEW_CART, {
          currency: this.getCurrency(cart),
          value: this.centsToMoney(cart?.total_price || 0),
          module_name: moduleName,
          items: items.map((item, index) => this.buildItemFromCartItem(item, {
            index,
            itemListId: 'cart',
            itemListName: 'Shopping Cart'
          }))
        });
      });
    }

    /**
     * 上报购物车商品列表曝光 view_item_list。
     * 与 view_cart 分开推送，满足商品列表曝光和购物车整体曝光两类需求。
     */
    trackCartItemList(moduleName) {
      this.fetchCart().then((cart) => {
        if (!cart || !cart.items || !cart.items.length) return;
        this.pushEvent(GA4_EVENTS.VIEW_ITEM_LIST, {
          item_list_name: 'Shopping Cart',
          item_list_id: 'cart',
          currency: this.getCurrency(cart),
          module_name: moduleName,
          items: cart.items.map((item, index) => this.buildItemFromCartItem(item, {
            index,
            itemListId: 'cart',
            itemListName: 'Shopping Cart'
          }))
        });
      });
    }

    /**
     * 监听侧栏购物车打开状态。
     * 通过 #CartDrawer 的 class/style 变化判断是否从关闭变为可见，打开时触发 view_cart 和 view_item_list。
     */
    observeCartDrawer() {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer) return;
      if (drawer === this.observedCartDrawer) return;
      this.observedCartDrawer = drawer;

      // 每次 DOM 属性变化时重新判断侧栏是否可见，只有“刚打开”的瞬间触发曝光。
      const check = () => {
        const isOpen = this.isCartDrawerOpen(drawer);
        if (isOpen && !this.cartDrawerWasOpen) {
          this.trackCartDrawerOpen();
        }
        this.cartDrawerWasOpen = isOpen;
      };

      check();
      const observer = new MutationObserver(check);
      observer.observe(drawer, { attributes: true, attributeFilter: ['style', 'class'] });
      this.observers.push(observer);
    }

    /**
     * 判断侧栏购物车是否处于真实打开状态。
     * 优先读取 Alpine store，避免关闭动画期间元素仍可见时被误判为重新打开。
     */
    isCartDrawerOpen(drawer) {
      const miniCart = window.Alpine?.store?.('xMiniCart');
      if (typeof miniCart?.open === 'boolean') return miniCart.open;
      return this.isElementVisible(drawer);
    }

    /**
     * 侧栏购物车打开时统一触发购物车曝光。
     * 空购物车也需要上报 view_cart；商品列表曝光仍只在有商品时由 trackCartItemList 负责。
     */
    trackCartDrawerOpen() {
      const now = Date.now();
      if (now - this.lastCartDrawerOpenAt < 800) return;
      this.lastCartDrawerOpenAt = now;

      this.trackCartView('pop-up cart');
      this.trackCartItemList('pop-up cart');
    }

    /**
     * 监听首页 banner 曝光。
     * 只监听 Liquid 显式输出了 data-gtm-view-banner="true" 的元素；slide 的 DOM id 由 Liquid 按 section + index 自动生成。
     */
    observeBanners() {
      if (!this.isHomePage()) return;
      const banners = Array.from(document.querySelectorAll('[data-gtm-view-banner="true"]'))
        .filter((banner) => !banner.classList.contains('splide__slide--clone'));
      if (!banners.length) return;

      banners.forEach((banner) => {
        if (this.observedBannerElements.has(banner)) return;
        this.observedBannerElements.add(banner);

        this.observeSingleBannerExposure(banner);
        this.observeBannerCarouselChanges(banner);
      });
    }

    /**
     * 监听单个配置化 banner 元素进入视口。
     * IntersectionObserver 可用时按 50% 阈值触发；极老浏览器没有该 API 时，保留一次同步可见性检查作为降级。
     */
    observeSingleBannerExposure(banner) {
      if (!('IntersectionObserver' in window)) {
        this.trackBannerExposure(banner);
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.trackBannerExposure(entry.target);
        });
      }, { threshold: 0.5 });

      observer.observe(banner);
      this.observers.push(observer);
    }

    /**
     * 监听轮播 active/visible 状态变化。
     * 自动播放、箭头切换、分页切换通常会更新 slide 的 class/aria-hidden/style；变化后只重新检查当前这个配置化 banner 元素。
     */
    observeBannerCarouselChanges(banner) {
      const carousel = banner.closest('.splide, .x-splide');
      if (!carousel) return;

      const observer = new MutationObserver(() => {
        window.clearTimeout(this.bannerMutationTimers.get(banner));
        const timer = window.setTimeout(() => this.trackBannerExposure(banner), 80);
        this.bannerMutationTimers.set(banner, timer);
      });
      observer.observe(carousel, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden', 'style'],
        subtree: true
      });
      this.observers.push(observer);
    }

    /**
     * 真正推送 view_banner。
     * 只对配置化元素上报；同一个 id 只曝光一次，轮播回来不会重复刷。
     */
    trackBannerExposure(element) {
      if (!element || !this.isConfiguredBannerVisible(element)) return;

      const key = this.getBannerExposureKey(element);
      if (this.viewedBannerKeys.has(key)) return;
      this.viewedBannerKeys.add(key);

      this.pushEvent(GA4_EVENTS.VIEW_BANNER, {
        module_name: element.dataset.gtmModuleName || 'top banner',
        title: this.getBannerTitle(element),
        position: this.getBannerPosition(element)
      });
    }

    /**
     * 监听商品卡片曝光。
     * 针对 Shopify 商品卡和 PageFly 商品盒子；新插入的卡片会由 MutationObserver 再次调用本方法补绑。
     */
    observeProductLists() {
      if (!('IntersectionObserver' in window)) return;
      const cards = Array.from(document.querySelectorAll('.card-product, [data-product-id][data-pf-type="ProductBox"], [data-pet-parents-product-card]'))
        .filter((card) => !card.dataset.satellaiObservedProductCard);
      if (!cards.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.viewedElements.has(entry.target)) return;
          this.viewedElements.add(entry.target);
          this.trackProductCardView(entry.target);
        });
      }, { threshold: 0.5 });

      cards.forEach((card) => {
        card.dataset.satellaiObservedProductCard = 'true';
        observer.observe(card);
      });
      this.observers.push(observer);
    }

    /**
     * 上报单个商品卡片曝光。
     * 根据商品链接拉取产品数据，并结合所在模块生成 item_list_name/item_list_id。
     */
    async trackProductCardView(card) {
      const link = card.querySelector('a[href*="/products/"]');
      const product = await this.fetchProductInfoFromLink(link);
      if (!product) return;

      const moduleName = this.getProductModuleName(card);
      const itemListName = this.getItemListName(card, moduleName);
      const itemListId = this.slugify(itemListName);
      const item = this.buildItemFromProduct(product, {
        variantId: this.getVariantIdFromUrl(link.getAttribute('href') || ''),
        index: this.getElementPosition(card),
        itemListId,
        itemListName,
        quantity: 1
      });

      this.pushEvent(GA4_EVENTS.VIEW_ITEM_LIST, {
        item_list_name: itemListName,
        item_list_id: itemListId,
        currency: this.getCurrency(),
        module_name: moduleName,
        items: [item]
      });
    }

    /**
     * 给页面内 video 绑定播放进度监听。
     * 每个 video 只绑定一次，并在 videoStates 中记录 start/25/50/75/complete 的去重状态。
     */
    attachVideos() {
      const videos = Array.from(document.querySelectorAll('video')).filter((video) => {
        return !video.dataset.satellaiVideoAttached && video.dataset.gtmVideoIgnore !== 'true';
      });
      videos.forEach((video) => {
        video.dataset.satellaiVideoAttached = 'true';
        this.videoStates.set(video, { videoUrl: this.getVideoUrl(video), started: false, pendingStart: false, p25: false, p50: false, p75: false, complete: false });
        video.addEventListener('play', () => this.trackVideoStart(video));
        video.addEventListener('loadedmetadata', () => this.flushPendingVideoStart(video));
        video.addEventListener('durationchange', () => this.flushPendingVideoStart(video));
        video.addEventListener('timeupdate', () => this.trackVideoProgress(video));
        video.addEventListener('ended', () => this.trackVideoComplete(video));
      });
    }

    /**
     * 上报视频开始播放。
     * 同一个 video 元素只在第一次 play 时上报 video_start。
     */
    trackVideoStart(video) {
      const state = this.videoStates.get(video);
      if (!state) return;
      this.resetVideoStateIfSourceChanged(video, state);
      if (state.started) return;
      if (!this.hasVideoDuration(video)) {
        state.pendingStart = true;
        return;
      }
      state.started = true;
      state.pendingStart = false;
      this.pushEvent(GA4_EVENTS.VIDEO_START, this.getVideoParams(video));
    }

    /**
     * 部分弹窗视频 play 事件早于 metadata，先等到 duration 可读再补发 start，避免 video_duration=0。
     */
    flushPendingVideoStart(video) {
      const state = this.videoStates.get(video);
      if (!state || !state.pendingStart || state.started || !this.hasVideoDuration(video)) return;
      state.pendingStart = false;
      state.started = true;
      this.pushEvent(GA4_EVENTS.VIDEO_START, this.getVideoParams(video));
    }

    /**
     * 上报视频播放进度。
     * 只在播放中检查 25/50/75 三个节点，每个节点同一视频只上报一次。
     */
    trackVideoProgress(video) {
      const state = this.videoStates.get(video);
      if (!state || !video.duration || video.paused) return;
      this.resetVideoStateIfSourceChanged(video, state);

      const percent = Math.floor((video.currentTime / video.duration) * 100);
      [25, 50, 75].forEach((mark) => {
        const key = `p${mark}`;
        if (percent >= mark && !state[key]) {
          state[key] = true;
          this.pushEvent(GA4_EVENTS.VIDEO_PROGRESS, {
            ...this.getVideoParams(video),
            video_percent: mark
          });
        }
      });
    }

    /**
     * 上报视频播放完成。
     * ended 事件触发后发送 video_complete，并固定 video_percent=100。
     */
    trackVideoComplete(video) {
      const state = this.videoStates.get(video);
      if (!state) return;
      this.resetVideoStateIfSourceChanged(video, state);
      if (state.complete) return;
      state.complete = true;
      this.pushEvent(GA4_EVENTS.VIDEO_COMPLETE, {
        ...this.getVideoParams(video),
        video_percent: 100
      });
    }

    /**
     * 同一个 video DOM 可能被弹窗复用并切换 src。
     * 当播放地址变化时重置去重状态，确保新视频能重新上报 start/progress/complete。
     */
    resetVideoStateIfSourceChanged(video, state) {
      const videoUrl = this.getVideoUrl(video);
      if (!videoUrl || state.videoUrl === videoUrl) return;

      state.videoUrl = videoUrl;
      state.started = false;
      state.pendingStart = false;
      state.p25 = false;
      state.p50 = false;
      state.p75 = false;
      state.complete = false;
    }

    /**
     * 生成视频事件公共参数。
     * 统一输出 video_title、video_url、video_duration 和 module_name，供 start/progress/complete 复用。
     */
    getVideoParams(video) {
      const videoUrl = this.getVideoUrl(video);
      return {
        video_title: this.getVideoTitle(video),
        video_url: videoUrl,
        video_duration: this.getVideoDuration(video),
        module_name: this.getVideoModuleName(video),
        position: this.getVideoPosition(video)
      };
    }

    /**
     * 获取视频所在模块名。
     * 视频组件优先显式写入 data-gtm-module-name，避免轮播/弹窗复用 DOM 时被错误推断。
     */
    getVideoModuleName(video) {
      const explicitModule = video.getAttribute('data-gtm-module-name') ||
        video.closest?.('[data-gtm-module-name]')?.getAttribute('data-gtm-module-name') ||
        '';

      return explicitModule || this.getModuleName(video, this.isProductPage() ? 'product video' : 'home video');
    }

    /**
     * 获取视频标题。
     * 主题组件应在 video 上输出 data-gtm-video-title；旧组件的 data-video-title 作为兼容读取。
     */
    getVideoTitle(video) {
      return video.getAttribute('data-gtm-video-title') ||
        video.getAttribute('data-video-title') ||
        video.closest?.('[data-gtm-video-title]')?.getAttribute('data-gtm-video-title') ||
        video.getAttribute('title') ||
        this.getElementTitle(video) ||
        '';
    }

    /**
     * 获取视频在当前模块中的位置，从左到右从 1 开始。
     */
    getVideoPosition(video) {
      const value = video.getAttribute('data-gtm-video-position') ||
        video.closest?.('[data-gtm-video-position]')?.getAttribute('data-gtm-video-position') ||
        '';
      const position = Number.parseInt(value, 10);
      return Number.isFinite(position) && position > 0 ? position : '';
    }

    /**
     * 判断视频时长是否已经可用于埋点。
     */
    hasVideoDuration(video) {
      return Number.isFinite(video.duration) && video.duration > 0;
    }

    /**
     * 获取视频时长。只有 metadata 可读后才返回真实时长，否则返回 0。
     */
    getVideoDuration(video) {
      return this.hasVideoDuration(video) ? this.roundMoney(video.duration) : 0;
    }

    /**
     * 获取 video 实际播放地址。
     * currentSrc 可能在播放前为空，因此再回退到 video.src 和第一个 source[src]。
     */
    getVideoUrl(video) {
      return video.currentSrc || video.src || video.querySelector?.('source[src]')?.getAttribute('src') || '';
    }

    /**
     * 监听 PDP 变体切换。
     * 主题可能通过 history.pushState/replaceState 或表单 change 更新 variant，这里延迟调用 trackProductView 补发新变体曝光。
     */
    patchHistoryForVariantView() {
      if (!this.isProductPage()) return;
      ['pushState', 'replaceState'].forEach((method) => {
        const original = history[method];
        if (typeof original !== 'function') return;
        history[method] = (...args) => {
          const result = original.apply(history, args);
          window.setTimeout(() => this.trackProductView(), 50);
          return result;
        };
      });
      window.addEventListener('popstate', () => window.setTimeout(() => this.trackProductView(), 50));
      document.addEventListener('change', (event) => {
        if (event.target && event.target.closest('.variant-selects, variant-selects, .product-info')) {
          window.setTimeout(() => this.trackProductView(), 200);
        }
      });
    }

    /**
     * 为加购事件查找产品数据。
     * PDP 直接使用 window.__SHOPIFY_PRODUCT__；商品卡片优先通过链接取产品数据；最后从 /cart.js 里按 variant_id 兜底。
     */
    async getProductForAdd(productForm, variantId) {
      if (window.__SHOPIFY_PRODUCT__ && this.isProductPage()) return window.__SHOPIFY_PRODUCT__;
      const link = productForm?.querySelector('a[href*="/products/"]') || productForm?.closest('.card-product')?.querySelector('a[href*="/products/"]');
      const product = await this.fetchProductInfoFromLink(link);
      if (product) return product;

      const cart = await this.fetchCart();
      const cartItem = cart?.items?.find((item) => String(item.variant_id) === String(variantId));
      if (!cartItem) return null;

      return {
        id: cartItem.product_id,
        title: cartItem.product_title,
        type: cartItem.product_type,
        price: cartItem.price,
        variants: [{
          id: cartItem.variant_id,
          title: cartItem.variant_title,
          price: cartItem.price
        }]
      };
    }

    /**
     * 从商品链接获取产品信息。
     * 先解析 /products/{handle}，再交给 fetchProductInfo 做缓存和接口请求。
     */
    async fetchProductInfoFromLink(link) {
      if (!link) return null;
      return this.fetchProductInfoFromUrl(link.getAttribute('href') || '');
    }

    /**
     * 从商品 URL 获取产品信息。
     * 这个方法服务于 a 标签和 GemPages 非 a 标签图片点击，统一解析 handle 后走同一份缓存。
     */
    async fetchProductInfoFromUrl(href) {
      const handle = this.getProductHandleFromUrl(href || '');
      if (!handle) return null;
      return this.fetchProductInfo(handle);
    }

    /**
     * 请求 Shopify 产品 JSON。
     * 使用 /products/{handle}.js 获取产品和变体数据，并用 productCache 避免同一个 handle 重复请求。
     */
    async fetchProductInfo(handle) {
      if (this.productCache.has(handle)) return this.productCache.get(handle);
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return null;
        const product = await response.json();
        this.productCache.set(handle, product);
        return product;
      } catch (error) {
        this.log('fetch product failed', error);
        return null;
      }
    }

    /**
     * 请求当前购物车 JSON。
     * 使用 Shopify routes.root 拼接 cart.js，失败时返回 null，不阻塞用户原有交互。
     */
    async fetchCart() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;
        return response.json();
      } catch (error) {
        this.log('fetch cart failed', error);
        return null;
      }
    }

    /**
     * 从加购成功事件 detail 中提取接口返回的购物车 item。
     * 兼容单商品 response、批量 items、response.items 三种返回结构。
     */
    getAddedResponseItems(detail) {
      if (Array.isArray(detail.items)) return detail.items;
      if (Array.isArray(detail.response?.items)) return detail.response.items;
      if (detail.response && (detail.response.product_id || detail.response.product_title || detail.response.variant_id)) {
        return [detail.response];
      }
      return [];
    }

    /**
     * 将 Shopify 产品 JSON 转成 GA4 items[] 结构。
     * 用当前变体或指定 variantId 取价格/变体名，金额统一从 cents 转成数字。
     */
    buildItemFromProduct(product, options = {}) {
      const variant = this.getVariant(product, options.variantId);
      const price = this.centsToMoney(variant?.price || product.price || 0);
      const quantity = this.toNumber(options.quantity, 1) || 1;

      return {
        item_id: product.id || '',
        item_name: product.title || '',
        coupon: '',
        discount: 0,
        index: options.index ?? '',
        item_list_id: options.itemListId ?? '',
        item_list_name: options.itemListName ?? '',
        item_category: product.type || '',
        item_variant: variant ? `${variant.title || ''}_${variant.id || ''}` : '',
        item_brand: BRAND_NAME,
        price,
        quantity
      };
    }

    /**
     * 将 Shopify cart.js item 转成 GA4 items[] 结构。
     * 同时计算折扣、coupon、购物车列表信息，适用于 view_cart/remove_from_cart 等购物车事件。
     */
    buildItemFromCartItem(item, options = {}) {
      const originalPrice = this.centsToMoney(item.original_price || item.price || 0);
      const finalPrice = this.centsToMoney(item.final_price || item.price || 0);
      const discount = Math.max(0, originalPrice - finalPrice);
      const quantity = this.toNumber(options.quantity, item.quantity || 1) || 1;

      return {
        item_id: item.product_id || '',
        item_name: item.product_title || item.title || '',
        coupon: item.discounts && item.discounts.length ? item.discounts[0].title : '',
        discount: this.roundMoney(discount),
        index: options.index ?? '',
        item_list_id: options.itemListId ?? 'cart',
        item_list_name: options.itemListName ?? 'Shopping Cart',
        item_category: item.product_type || '',
        item_variant: item.variant_id ? `${item.variant_title || ''}_${item.variant_id}` : '',
        item_brand: BRAND_NAME,
        price: finalPrice,
        quantity
      };
    }

    /**
     * 从 liquid 注入的 data-gtm-* 属性生成 GA4 items[] 单项。
     * 主要用于侧栏购物车/购物车页的商品点击，避免点击跳转前再等待异步产品接口。
     */
    buildItemFromTrackingDataset(element, options = {}) {
      const source = element?.closest?.('[data-gtm-cart-item]');
      if (!source?.dataset?.gtmItemId && !source?.dataset?.gtmItemName) return null;

      const finalPrice = this.centsToMoney(source.dataset.gtmPrice);
      const originalPrice = this.centsToMoney(source.dataset.gtmOriginalPrice || source.dataset.gtmPrice);
      const discount = Math.max(0, originalPrice - finalPrice);

      return {
        item_id: source.dataset.gtmItemId || '',
        item_name: source.dataset.gtmItemName || '',
        coupon: source.dataset.gtmCoupon || '',
        discount: this.roundMoney(discount),
        index: options.index ?? this.toNumber(source.dataset.gtmIndex, ''),
        item_list_id: options.itemListId ?? source.dataset.gtmItemListId ?? '',
        item_list_name: options.itemListName ?? source.dataset.gtmItemListName ?? '',
        item_category: source.dataset.gtmItemCategory || '',
        item_variant: source.dataset.gtmItemVariant || '',
        item_brand: source.dataset.gtmItemBrand || BRAND_NAME,
        price: finalPrice,
        quantity: this.toNumber(source.dataset.gtmQuantity, 1) || 1
      };
    }

    /**
     * 从产品数据中选择变体。
     * 优先匹配传入 variantId；没有传入时读取当前 URL/产品默认变体；仍找不到则返回第一个变体。
     */
    getVariant(product, variantId) {
      const variants = product?.variants || [];
      if (!variants.length) return null;
      const id = variantId || this.getCurrentVariantId(product);
      return variants.find((variant) => String(variant.id) === String(id)) || variants[0];
    }

    /**
     * 获取当前 PDP 选中的变体 ID。
     * 读取顺序为 URL variant 参数、产品对象上的 selected 字段、首个可售变体。
     */
    getCurrentVariantId(product) {
      const params = new URLSearchParams(window.location.search);
      return params.get('variant') || product?.selected_variant_id || product?.selected_or_first_available_variant?.id || product?.variants?.[0]?.id || '';
    }

    /**
     * 从商品 URL 中解析 handle。
     * 支持绝对 URL 和相对 URL；解析失败时用正则兜底。
     */
    getProductHandleFromUrl(href) {
      try {
        const url = new URL(href, window.location.origin);
        const match = url.pathname.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      } catch (error) {
        const match = String(href).match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      }
    }

    /**
     * 从商品链接里解析 variant 参数。
     * 自定义活动页的 Learn More/图片链接可能指定变体，曝光和点击都要用这个变体生成价格与 item_variant。
     */
    getVariantIdFromUrl(href) {
      try {
        const url = new URL(href, window.location.origin);
        return url.searchParams.get('variant') || '';
      } catch (error) {
        const match = String(href).match(/[?&]variant=([^&#]+)/);
        return match ? decodeURIComponent(match[1]) : '';
      }
    }

    /**
     * 统一向 dataLayer 推送 GA4 事件。
     * 每次先推 `{ event_parameters: null }` 清空上一次参数，再推固定 event=ga4Event 和业务 event_name。
     */
    pushEvent(eventName, eventParameters = {}, extra = {}) {
      if (!eventName) {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event_parameters: null });
      const payload = {
        event: DATA_LAYER_EVENT,
        event_name: eventName,
        event_parameters: this.cleanObject(eventParameters),
        ...extra
      };
      window.dataLayer.push(payload);
      this.log('push', payload);
    }

    /**
     * 清理上报参数。
     * 递归移除 undefined、null、空字符串，保留 0/false，避免 dataLayer 中出现无意义字段。
     */
    cleanObject(value) {
      if (Array.isArray(value)) {
        return value.map((item) => this.cleanObject(item));
      }
      if (value && typeof value === 'object') {
        return Object.entries(value).reduce((result, [key, item]) => {
          if (item !== undefined && item !== null && item !== '') {
            result[key] = this.cleanObject(item);
          }
          return result;
        }, {});
      }
      return value;
    }

    /**
     * 安全解析元素上的 JSON 属性。
     * GemPages 的 gp-data 会很长，并且可能带 HTML entity；先直接 JSON.parse，失败后再做一次实体解码。
     */
    parseJsonAttribute(element, attributeName) {
      const raw = element?.getAttribute?.(attributeName);
      if (!raw) return null;

      try {
        return JSON.parse(raw);
      } catch (error) {
        try {
          const textarea = document.createElement('textarea');
          textarea.innerHTML = raw;
          return JSON.parse(textarea.value);
        } catch (decodeError) {
          this.log('parse json attribute failed', attributeName, decodeError);
          return null;
        }
      }
    }

    /**
     * 推断元素所在模块名。
     * 优先取最近 section 的标题；没有标题时使用 section id 清洗后的名称；仍没有则返回 fallback。
     */
    getModuleName(element, fallback = 'unknown module') {
      if (!element) return fallback;
      const explicitModule = element.closest?.('[data-gtm-module-name]')?.dataset?.gtmModuleName;
      if (explicitModule) return explicitModule.slice(0, 120);

      const section = element.closest('.shopify-section, section, [data-section-id]');
      const heading = section?.querySelector('h1, h2, h3, .h1, .h2, .h3, [data-section-heading]');
      const headingText = this.getText(heading);
      if (headingText) return headingText.slice(0, 120);

      const id = section?.id || section?.dataset?.sectionId || '';
      if (id) {
        return id
          .replace(/^shopify-section-template--\d+__/, '')
          .replace(/^shopify-section-/, '')
          .replace(/[_-]+/g, ' ')
          .trim() || fallback;
      }
      return fallback;
    }

    /**
     * 推断商品相关事件的 module_name。
     * 购物车侧栏、购物车页、PDP、集合页分别使用固定名称，其余商品列表回退到 section 标题。
     */
    getProductModuleName(element) {
      if (!element) return this.isCartPage() ? 'cart page' : 'product list';
      if (element.closest('#CartDrawer')) return 'pop-up cart';
      if (element.closest('#main-cart-items') || this.isCartPage()) return 'cart page';
      if (this.isProductPage()) return 'product details';
      if (this.isCollectionPage()) return 'collection page';
      return this.getModuleName(element, 'product list');
    }

    /**
     * 推断 GA4 item_list_name。
     * 购物车相关元素统一为 Shopping Cart；其他列表使用模块名或 Product List。
     */
    getItemListName(element, fallback) {
      if (element?.closest('#CartDrawer, #main-cart-items') || this.isCartPage()) return 'Shopping Cart';
      return this.getModuleName(element, fallback || 'Product List');
    }

    /**
     * 推断 add_to_cart 的 module_name。
     * 侧栏购物车/购物车页有固定值；PDP 上特殊的 will it work 模块单独命名，其余按 product details。
     */
    getAddToCartModuleName(productForm) {
      if (productForm?.closest('#CartDrawer')) return 'pop-up cart';
      if (productForm?.closest('#main-cart-items') || this.isCartPage()) return 'cart page';
      const text = this.getModuleName(productForm, 'product details').toLowerCase();
      if (text.includes('will it work')) return 'will it work for me';
      return 'product details';
    }

    /**
     * 获取购物车相关事件的基础模块名。
     * 当前页面是 /cart 时返回 cart page，否则按侧栏购物车 pop-up cart 处理。
     */
    getCartModuleBaseName() {
      return this.isCartPage() ? 'cart page' : 'pop-up cart';
    }

    /**
     * 获取加购按钮名。
     * 优先使用成功事件传入的 clickedButton，其次在表单内找常见加购按钮，最后回退为 Add to cart。
     */
    getAddToCartButtonName(productForm, clickedButton) {
      const button = clickedButton || productForm?.querySelector('.add_to_cart_button, .add_to_card_access, [type="submit"], button');
      return this.getButtonName(button) || 'Add to cart';
    }

    /**
     * 判断一个控件是否是加购入口。
     * 该函数只用于普通 button_click 的排除，避免“点击加购按钮”先被泛化按钮埋点抢走。
     */
    isAddToCartControl(control) {
      if (!control) return false;
      const formId = control.getAttribute?.('form');
      const linkedForm = formId ? document.getElementById(formId) : null;
      const ownForm = control.closest?.('form');
      const form = linkedForm || ownForm;
      const action = form?.getAttribute?.('action') || '';
      const buttonName = this.getButtonName(control).toLowerCase();
      const className = String(control.className || '').toLowerCase();
      const ctaId = control.getAttribute?.('data-cta-id') || '';

      return (
        className.includes('add_to_cart') ||
        className.includes('add-to-cart') ||
        ctaId === 'add_to_cart' ||
        control.getAttribute?.('name') === 'add' ||
        control.closest?.('[x-data^="xProductCart"]') ||
        form?.dataset?.type === 'add-to-cart-form' ||
        action.includes('/cart/add') ||
        buttonName.includes('add to cart') ||
        buttonName.includes('pre-order') ||
        buttonName.includes('pre order')
      );
    }

    /**
     * 提取按钮或链接的可读名称。
     * 输入框取 value/aria-label；普通元素优先取可见文本，再取 aria/title；纯链接兜底为 a link。
     */
    getButtonName(element) {
      if (!element) return '';
      if (element instanceof HTMLInputElement) return element.value || element.getAttribute('aria-label') || '';
      const aria = element.getAttribute?.('aria-label');
      const text = this.getText(element);
      if (text) return text;
      if (aria) return aria;
      const title = element.getAttribute?.('title');
      if (title) return title;
      const href = element.getAttribute?.('href') || '';
      if (href) return 'a link';
      return '';
    }

    /**
     * 获取元素可见/文本内容。
     * 会折叠多余空白，保证 button_name/module_name 不带换行和连续空格。
     */
    getText(element) {
      if (!element) return '';
      return (element.innerText || element.textContent || '').replace(/\s+/g, ' ').trim();
    }

    /**
     * 获取模块或媒体标题。
     * 优先查找常见标题元素，其次读取 data-title 或视频埋点标题标记。
     */
    getElementTitle(element) {
      const titleEl = element?.querySelector?.('h1, h2, h3, .h1, .h2, .h3, [data-title], [data-gtm-video-title], [data-video-title]');
      return this.getText(titleEl) || element?.dataset?.title || element?.getAttribute?.('data-gtm-video-title') || element?.getAttribute?.('data-video-title') || '';
    }

    /**
     * 计算元素在同级候选元素中的位置。
     * 用于 banner position、商品 index；找不到有效同级时默认返回 1。
     */
    getElementPosition(element) {
      if (!element?.parentElement) return 1;
      const siblings = Array.from(element.parentElement.children).filter((child) => child.matches?.('.card-product, section, [class*="banner"], li, .splide__slide, .x-splide-slide'));
      const index = siblings.indexOf(element);
      return index >= 0 ? index + 1 : 1;
    }

    /**
     * 生成 banner 曝光去重 key。
     * 首页轮播 slide 会输出自动生成的 DOM id，因此优先用 id 去重；没有 id 时再用标题和位置兜底。
     */
    getBannerExposureKey(element) {
      return element.id || `${this.getBannerTitle(element)}:${this.getBannerPosition(element)}`;
    }

    /**
     * 获取 banner title。
     * 优先读取 Liquid 输出的 data-gtm-title，保证 JS 不需要从复杂 DOM 里猜标题。
     */
    getBannerTitle(element) {
      return element?.dataset?.gtmTitle || this.getElementTitle(element);
    }

    /**
     * 获取 banner position。
     * 优先读取 Liquid 输出的 data-gtm-position，缺失时退回同级位置。
     */
    getBannerPosition(element) {
      const position = Number(element?.dataset?.gtmPosition);
      return Number.isFinite(position) && position > 0 ? position : this.getElementPosition(element);
    }

    /**
     * 判断配置化 banner 是否真的处于可曝光状态。
     * 轮播初始化后优先尊重 is-active/is-visible/aria-hidden；未初始化时只要元素 50% 进入视口即可。
     */
    isConfiguredBannerVisible(element) {
      if (!element || !this.isElementInViewportByRatio(element, 0.5)) return false;
      if (element.classList.contains('splide__slide--clone')) return false;

      const ariaHidden = element.getAttribute('aria-hidden');
      if (ariaHidden === 'true') return false;

      const carousel = element.closest('.splide, .x-splide');
      if (!carousel) return true;

      const hasCarouselState = (
        carousel.classList.contains('is-initialized') ||
        carousel.classList.contains('is-rendered') ||
        ariaHidden !== null ||
        element.classList.contains('is-active') ||
        element.classList.contains('is-visible')
      );
      if (!hasCarouselState) return true;

      return (
        element.classList.contains('is-active') ||
        element.classList.contains('is-visible') ||
        ariaHidden === 'false'
      );
    }

    /**
     * 从点击目标向上查找所属 banner。
     * 只在 main 下的 section/shopify-section 且命中 banner/hero/slideshow 关键词时返回。
     */
    getBannerElement(element) {
      const banner = element.closest('main section, main .shopify-section');
      if (!banner) return null;
      const label = `${banner.id || ''} ${banner.className || ''}`.toLowerCase();
      return label.includes('banner') || label.includes('hero') || label.includes('slideshow') ? banner : null;
    }

    /**
     * 判断链接是否是社交媒体链接。
     * 用于 footer 中区分 follow_us 和普通 bottom navigation。
     */
    isSocialLink(href) {
      return /facebook|instagram|tiktok|youtube|twitter|x\.com|pinterest|linkedin/i.test(href);
    }

    /**
     * 从社交链接中提取平台名。
     * x.com 统一规范为 x，其他平台返回匹配到的域名关键词。
     */
    getSocialName(href) {
      const match = String(href).match(/facebook|instagram|tiktok|youtube|twitter|x\.com|pinterest|linkedin/i);
      return match ? match[0].replace('x.com', 'x') : '';
    }

    /**
     * 获取 footer 链接所属栏目名。
     * 从最近 footer 列或列表项里找标题，作为 bottom navigation 的 first_navigation。
     */
    getFooterGroupName(link) {
      const navColumn = link.closest('.footer-nav-col');
      const navHeading = navColumn?.querySelector('.footer-nav-title-text');
      if (navHeading) return this.getText(navHeading);

      const column = link.closest('[class*="footer-col"], .footer-col');
      const heading = column?.querySelector('p, h2, h3, .h6');
      return this.getText(heading);
    }

    /**
     * 获取当前币种。
     * 优先使用 cart.js 返回的 currency，其次是 theme.liquid 注入上下文，再退回 Shopify 当前币种和 USD。
     */
    getCurrency(cart) {
      return cart?.currency || this.context.currency || window.Shopify?.currency?.active || 'USD';
    }

    /**
     * Shopify cents 金额转普通金额数字。
     * 所有 price/value/discount 都通过这里统一转成两位小数。
     */
    centsToMoney(cents) {
      const number = this.toNumber(cents, 0);
      return this.roundMoney(number / 100);
    }

    /**
     * 金额四舍五入到两位小数。
     * 先做数字转换，保证字符串金额也能稳定处理。
     */
    roundMoney(value) {
      return Number((this.toNumber(value, 0)).toFixed(2));
    }

    /**
     * 安全数字转换。
     * Number(value) 为有限数字时返回转换结果，否则返回 fallback。
     */
    toNumber(value, fallback = 0) {
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    }

    /**
     * 将列表名转换成稳定的 item_list_id。
     * 小写化后把非字母数字替换成短横线，空值回退为 list。
     */
    slugify(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'list';
    }

    /**
     * 判断元素是否在页面上可见。
     * 结合 display、visibility 和 getClientRects，用于侧栏购物车打开状态判断。
     */
    isElementVisible(element) {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    }

    /**
     * 判断元素在当前视口中的可见面积比例。
     * 用面积比例而不是只看 top/bottom，可以同时兼容横向轮播、fade 轮播和不同屏宽下的 banner 裁切。
     */
    isElementInViewportByRatio(element, threshold = 0.5) {
      if (!this.isElementVisible(element)) return false;
      const rect = element.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const width = Math.max(rect.width, 0);
      const height = Math.max(rect.height, 0);
      if (!width || !height) return false;

      const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      const visibleRatio = (visibleWidth * visibleHeight) / (width * height);
      return visibleRatio >= threshold;
    }

    /**
     * 判断当前是否首页。
     * 同时兼容 URL 路径和 theme.liquid 注入的 Shopify pageType。
     */
    isHomePage() {
      return window.location.pathname === '/' || this.context.pageType === 'index';
    }

    /**
     * 判断当前是否商品详情页。
     * 同时兼容 /products/ 路径和 Shopify pageType=product。
     */
    isProductPage() {
      return window.location.pathname.includes('/products/') || this.context.pageType === 'product';
    }

    /**
     * 判断当前是否集合/列表页。
     * 同时兼容 /collections/ 路径和 Shopify pageType=collection。
     */
    isCollectionPage() {
      return window.location.pathname.includes('/collections/') || this.context.pageType === 'collection';
    }

    /**
     * 判断当前是否购物车页。
     * 同时兼容 /cart 路径和 Shopify pageType=cart。
     */
    isCartPage() {
      return window.location.pathname === '/cart' || this.context.pageType === 'cart';
    }

    /**
     * 计算字符串 SHA-256。
     * 当前文件保留该工具函数给后续 user_id hash 使用；浏览器不支持 crypto.subtle 时返回原字符串。
     */
    async sha256(str) {
      if (!window.crypto?.subtle) return str;
      const encoder = new TextEncoder();
      const data = encoder.encode(String(str));
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    /**
     * debug 日志输出。
     * 只有 theme.liquid 注入 context.debug=true 时才打印，避免线上默认产生控制台噪音。
     */
    log(...args) {
      if (this.debug) console.log('[SATELLAI GA4]', JSON.stringify([...args]));
    }
  }

  window.SatellaiGA4Tracking = SatellaiGA4Tracking;

  /**
   * DOM 就绪后创建全局单例。
   * window.satellaiGA4TrackingInstance 用于避免脚本被重复加载时重复绑定监听。
   */
  const init = () => {
    if (window.satellaiGA4TrackingInstance) return;
    window.satellaiGA4TrackingInstance = new SatellaiGA4Tracking();
    window.satellaiGA4TrackingInstance.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
